/**
 * Alinq protocol — shared types and trust-tier rules.
 *
 * This module is pure data + pure functions: no I/O, no crypto, no runtime deps.
 * It is the single source of truth for the message formats used by the relay
 * (phase 2), the SDK + PSI scheduling (phase 3) and the social connectors (phase 4).
 *
 * Two layers of message travel between two agents, and they must never be confused:
 *   - RelayEnvelope  — the OUTER routing format. The ONLY thing the relay sees.
 *                      Its `ciphertext` is opaque to the relay (XChaCha20-Poly1305).
 *   - Message        — the INNER plaintext, readable only by the two endpoints
 *                      after the handshake. Either an application `Payload` or a
 *                      connection-management `LinkPayload`.
 */

/** W3C did:key based on an Ed25519 signing key. Example: "did:key:z6Mk...". */
export type DID = string;

/**
 * The minimal, publicly shareable agent profile. Contains NO private data.
 * Published in the directory (as a SignedAgentCard) and exchanged during the
 * capability handshake.
 */
export interface AgentCard {
  /** Stable cryptographic identity. */
  did: DID;
  /** Human-facing label. Not sensitive. */
  displayName: string;
  /** Supported actions, e.g. ["schedule.match"]. */
  capabilities: string[];
  /** Where this agent is reachable, e.g. "wss://relay.alinq.ai". */
  relayEndpoint: string;
  publicKeys: {
    /** base64url Ed25519 public key (also encoded inside the DID). */
    signing: string;
    /** base64url X25519 public key, used for ECDHE during the handshake. */
    keyAgreement: string;
  };
}

/**
 * Trust tiers — the layered relationship levels.
 *   0 Connection — first meeting; only the card.
 *   1 Friend     — availability (PSI), introductions, scheduling.
 *   2 Business   — business availability, quotes/requests, referrals.
 *   3 Financial  — transactions on your behalf (LATER phase; not built here).
 * The level is recorded, co-signed, in the relationship state on both sides.
 * No unilateral upgrade is possible.
 */
export type TrustTier = 0 | 1 | 2 | 3;

export const TRUST_TIER_NAMES: Record<TrustTier, string> = {
  0: "Connection",
  1: "Friend",
  2: "Business",
  3: "Financial",
};

// ---------------------------------------------------------------------------
// Inner channel — application payloads (readable only by the two endpoints)
// ---------------------------------------------------------------------------

/** Application payloads that flow inside the encrypted channel. */
export type Payload =
  // capability.hello/ack also carry a fresh per-session ephemeral X25519 public
  // key (base64url) so the two endpoints can derive a forward-secret session key.
  | { type: "capability.hello"; card: AgentCard; ephemeralKey: string }
  | { type: "capability.ack"; card: AgentCard; ephemeralKey: string }
  | { type: "task.request"; id: string; action: string; args: unknown; tier: TrustTier }
  | { type: "task.result"; id: string; ok: boolean; result?: unknown; error?: string }
  | { type: "psi.round1"; sessionId: string; blindedPoints: string[] }
  | { type: "psi.round2"; sessionId: string; doubleBlinded: string[]; blindedReply: string[] }
  | { type: "psi.result"; sessionId: string; intersection: string[] }
  | { type: "error"; code: string; message: string };

export type PayloadType = Payload["type"];

/**
 * Minimum relationship tier required to ACCEPT an incoming application payload.
 * An agent MUST refuse any application action above the relationship's tier.
 * Connection-management messages (LinkPayload) and the auth handshake are NOT
 * listed here — they are governed by the pairing code and the co-signature,
 * not by an existing tier (a link is what establishes the tier in the first
 * place).
 */
export const MIN_TIER: Record<PayloadType, TrustTier> = {
  "capability.hello": 0,
  "capability.ack": 0,
  "task.request": 1,
  "task.result": 1,
  "psi.round1": 1,
  "psi.round2": 1,
  "psi.result": 1,
  error: 0,
};

// ---------------------------------------------------------------------------
// Inner channel — connection management (the Alinq Address + link flow)
// ---------------------------------------------------------------------------

/** The human-facing address: a handle backed by a DID. */
export interface AlinqAddress {
  /** "@username#tag", e.g. "@sam#7F3A". */
  handle: string;
  /** Resolves to the cryptographic identity. */
  did: DID;
}

/**
 * An AgentCard plus its human-readable handle, self-signed by the owner's DID.
 * Anyone can verify the binding handle <-> card against the DID; the directory
 * cannot forge it.
 */
export interface SignedAgentCard extends AgentCard {
  /** The human-readable address, "@username#tag". */
  handle: string;
  /** base64url Ed25519 self-signature over the canonical card (handle included). */
  signature: string;
}

/** A short, single-use, time-limited code that gates who may link and at what tier. */
export interface PairingCode {
  /** Single-use, e.g. 8 chars base32. */
  code: string;
  /** Starting tier this code grants (the granted tier may be lower, never higher). */
  tier: TrustTier;
  /** Expiry, epoch milliseconds. */
  expiresAt: number;
}

/**
 * A relationship record, co-signed by both DIDs so neither side can
 * unilaterally claim or upgrade the tier.
 */
export interface SignedRelationship {
  a: DID;
  b: DID;
  tier: TrustTier;
  /** epoch milliseconds */
  establishedAt: number;
  /** base64url Ed25519 signature by `a` over the canonical relationship core. */
  sigA: string;
  /** base64url Ed25519 signature by `b` over the canonical relationship core. */
  sigB: string;
}

/** Connection-management messages. Authorized by the pairing code / co-signature. */
export type LinkPayload =
  | { type: "link.request"; from: SignedAgentCard; requestedTier: TrustTier; codeProof?: string }
  | { type: "link.accept"; grantedTier: TrustTier; relationship: SignedRelationship }
  | { type: "link.decline"; reason?: string }
  | { type: "link.revoke"; reason?: string }
  | { type: "link.downgrade"; tier: TrustTier; relationship: SignedRelationship };

export type LinkPayloadType = LinkPayload["type"];

/** The full set of inner-channel messages exchanged between two endpoints. */
export type Message = Payload | LinkPayload;

// ---------------------------------------------------------------------------
// Outer channel — relay routing (the only thing the relay sees)
// ---------------------------------------------------------------------------

/**
 * The outer routing envelope. The relay routes on `from`/`to` and forwards
 * `ciphertext` verbatim. It can NEVER read the content.
 */
export interface RelayEnvelope {
  type: "envelope";
  from: DID;
  to: DID;
  /** base64url, 24 bytes for XChaCha20. */
  nonce: string;
  /** base64url, XChaCha20-Poly1305 over a JSON-encoded Message. */
  ciphertext: string;
}

// ---------------------------------------------------------------------------
// Relay authentication (prove ownership of the DID)
// ---------------------------------------------------------------------------

export interface AuthChallenge {
  type: "auth.challenge";
  /** base64url random nonce the client must sign. */
  nonce: string;
}

export interface AuthResponse {
  type: "auth.response";
  did: DID;
  /** base64url Ed25519 signature over the challenge nonce. */
  signature: string;
}

export interface AuthOk {
  type: "auth.ok";
  did: DID;
}

/** Everything a client can send to the relay over the wire. */
export type RelayClientMessage = AuthResponse | RelayEnvelope;
/** Everything the relay can send to a client over the wire. */
export type RelayServerMessage =
  | AuthChallenge
  | AuthOk
  | RelayEnvelope
  | { type: "error"; code: string; message: string };

// ---------------------------------------------------------------------------
// Trust-tier guard — fail closed
// ---------------------------------------------------------------------------

/**
 * Whether `type` is a known application payload type that carries a tier rule.
 */
export function isApplicationPayloadType(type: string): type is PayloadType {
  return Object.prototype.hasOwnProperty.call(MIN_TIER, type);
}

/**
 * The minimum tier required to accept an application payload of `type`.
 * Unknown types fail closed: the highest tier is required, so an unrecognised
 * action is refused at every real relationship level except an explicit 3.
 * Callers should additionally reject unknown types outright.
 */
export function minTierFor(type: string): TrustTier {
  return isApplicationPayloadType(type) ? MIN_TIER[type] : 3;
}

/**
 * Decide whether an agent at `relationshipTier` may accept an application
 * payload of `type`. Fail closed: unknown types are refused below tier 3, and
 * any action whose required tier exceeds the relationship tier is refused.
 */
export function isActionAllowed(type: string, relationshipTier: TrustTier): boolean {
  if (!isApplicationPayloadType(type)) return false;
  return relationshipTier >= MIN_TIER[type];
}
